import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, buscar } from '../utils/formato';

// Los arrendatarios.
//
// La ficha de la persona o la empresa, sin más. Lo que deben no vive aquí sino
// en el contrato: la misma persona puede tener dos locales, uno al día y otro
// atrasado, y un saldo pegado a la persona no distinguiría cuál.

const VACIO = { nombre: '', documento: '', telefono: '', email: '', direccion: '', nota: '' };

function Arrendatarios({ irA, onCambio }) {
  const [lista, setLista] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/arrendatarios').then(r => setLista(r.data || [])).catch(() => {});
    axios.get('/api/contratos').then(r => setContratos(r.data || [])).catch(() => {});
  }, []);

  useEffect(cargar, [cargar]);

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      if (editando.id) await axios.put(`/api/arrendatarios/${editando.id}`, editando);
      else await axios.post('/api/arrendatarios', editando);
      setAviso({ tipo: 'bueno', texto: `${editando.nombre} quedó guardado` });
      setEditando(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar' });
    }
    setGuardando(false);
  };

  const suyos = (id) => contratos.filter(c => c.arrendatario_id === id);
  const visibles = buscar(lista, busqueda, ['nombre', 'documento', 'telefono']);

  return (
    <>
      <div className="encabezado">
        <h1>Arrendatarios</h1>
        <p>Quién ocupa los inmuebles</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      <div className="bloque">
        <h2>👥 Arrendatarios <span className="contador">{lista.length}</span></h2>
        <div className="cuerpo">
          <div className="acciones" style={{ marginBottom: 14 }}>
            <button className="boton" onClick={() => setEditando({ ...VACIO })}>
              ➕ Nuevo arrendatario
            </button>
          </div>

          {lista.length > 5 && (
            <div className="buscador">
              <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
                     placeholder="🔎 Buscar por nombre, cédula o teléfono" />
              {busqueda && <button type="button" onClick={() => setBusqueda('')}>×</button>}
            </div>
          )}

          {visibles.length === 0 ? (
            <div className="vacio">
              {lista.length === 0
                ? 'Todavía no hay arrendatarios. Crea el primero para poder hacer un contrato.'
                : 'Ninguno coincide con esa búsqueda.'}
            </div>
          ) : (
            <div className="tabla-caja">
              <table className="datos">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Cédula o NIT</th>
                    <th>Teléfono</th>
                    <th>Contratos</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map(a => {
                    const cs = suyos(a.id);
                    const activos = cs.filter(c => c.estado === 'activo');
                    const debe = activos.reduce(
                      (s, c) => s + (c.estado_cuenta?.saldo > 0 ? c.estado_cuenta.saldo : 0), 0);
                    return (
                      <tr key={a.id}>
                        <td className="recio">
                          {a.nombre}
                          {a.email && (
                            <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                              {a.email}
                            </div>
                          )}
                        </td>
                        <td className="codigo">{a.documento || '—'}</td>
                        <td>{a.telefono || '—'}</td>
                        <td style={{ fontSize: 13 }}>
                          {activos.length > 0 ? (
                            <>
                              {activos.map(c => (
                                <div key={c.id}>{c.inmueble}</div>
                              ))}
                              {debe > 0 && (
                                <span className="pinta malo">debe {plata(debe)}</span>
                              )}
                            </>
                          ) : cs.length > 0 ? (
                            <span className="pinta gris">{cs.length} terminados</span>
                          ) : (
                            <span style={{ color: 'var(--tinta-tenue)' }}>ninguno</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="boton chico suave"
                                  onClick={() => setEditando({ ...VACIO, ...a })}>
                            ✏️
                          </button>{' '}
                          {activos.length === 0 && (
                            <button className="boton chico" onClick={() => irA('contratos')}>
                              Arrendarle
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editando && (
        <div className="bloque">
          <h2>{editando.id ? '✏️ Editar' : '➕ Nuevo arrendatario'}</h2>
          <form onSubmit={guardar}>
            <div className="cuerpo">
              <div className="campos">
                <label className="campo">
                  <span>Nombre *</span>
                  <input value={editando.nombre} required autoFocus
                         placeholder="Persona o empresa"
                         onChange={e => setEditando({ ...editando, nombre: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Cédula o NIT</span>
                  <input value={editando.documento || ''}
                         onChange={e => setEditando({ ...editando, documento: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Teléfono</span>
                  <input value={editando.telefono || ''}
                         onChange={e => setEditando({ ...editando, telefono: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Correo</span>
                  <input type="email" value={editando.email || ''}
                         onChange={e => setEditando({ ...editando, email: e.target.value })} />
                </label>
              </div>
              <label className="campo" style={{ marginTop: 12 }}>
                <span>Nota</span>
                <input value={editando.nota || ''} placeholder="Lo que convenga recordar"
                       onChange={e => setEditando({ ...editando, nota: e.target.value })} />
              </label>
              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setEditando(null)}>
                  Cancelar
                </button>
                <button className="boton" disabled={guardando}>
                  {guardando ? 'Guardando…' : '💾 Guardar'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default Arrendatarios;
